import { useState } from 'react'
import type { MountedBrain, AgentCard } from '../../types/brain'

interface BrainCardProps {
  mount: MountedBrain
  card?: AgentCard
  onChat: (brainId: string) => void
  onRemount: (brainId: string) => Promise<void>
}

function timeAgo(isoString: string): string {
  const ms = Date.now() - new Date(isoString).getTime()
  const minutes = Math.floor(ms / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export function BrainCard({ mount, card, onChat, onRemount }: BrainCardProps) {
  const isStale = !mount.config_hash_current
  const [remounting, setRemounting] = useState(false)
  const [remountError, setRemountError] = useState<string | null>(null)

  const handleRemount = async () => {
    setRemounting(true)
    setRemountError(null)
    try {
      await onRemount(mount.brain_id)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Re-mount failed'
      setRemountError(msg)
    } finally {
      setRemounting(false)
    }
  }

  return (
    <div className="border border-swiss-gray-200 p-5 flex flex-col gap-4">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="font-mono text-[14px] font-semibold text-swiss-black tracking-tight">
            {mount.brain_id}
          </span>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[11px] px-1.5 py-0.5 bg-swiss-gray-100 text-swiss-gray-500 font-medium">
              {mount.environment}
            </span>
            <span
              className={`text-[11px] px-1.5 py-0.5 font-medium ${
                mount.approval_flow_mechanism === 'mech-plane'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-blue-50 text-blue-700'
              }`}
            >
              {mount.approval_flow_mechanism}
            </span>
            {isStale && (
              <span className="text-[11px] px-1.5 py-0.5 bg-amber-50 text-amber-700 font-medium">
                stale config
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      {card?.description && (
        <p className="text-[13px] text-swiss-gray-500 leading-relaxed">{card.description}</p>
      )}

      {/* Meta row */}
      <div className="flex items-center gap-4 text-[12px] text-swiss-gray-400">
        {card && (
          <span>{card.skills.length} skill{card.skills.length !== 1 ? 's' : ''}</span>
        )}
        <span>mounted {timeAgo(mount.mounted_at)}</span>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 pt-1">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onChat(mount.brain_id)}
            className="text-[13px] font-medium px-3 py-1.5 bg-swiss-black text-swiss-white cursor-pointer transition-colors duration-150 hover:bg-swiss-gray-800"
          >
            Chat →
          </button>
          {isStale && (
            <button
              onClick={handleRemount}
              disabled={remounting}
              className={`text-[13px] px-3 py-1.5 border transition-colors duration-150 ${
                remounting
                  ? 'text-swiss-gray-400 border-swiss-gray-200 cursor-not-allowed'
                  : 'text-swiss-gray-600 border-swiss-gray-300 hover:border-swiss-gray-500 hover:text-swiss-black cursor-pointer'
              }`}
            >
              {remounting ? 'Re-mounting…' : 'Re-mount'}
            </button>
          )}
        </div>
        {remountError && (
          <p role="alert" className="text-[12px] text-red-600">{remountError}</p>
        )}
      </div>
    </div>
  )
}
